// docs/31 fog of war, the client's pure half: what the FogLayer draws is computed here from the
// `FogView` the sim hands the party (cells / lit / eyes / marks), so it can be unit-tested without
// Phaser. Nothing here decides who sees what — that is `fk_sim::fog`; this only shapes the picture.
//
//   fogMasks      the shader's two mask textures at SUB texels per square: A = in sight (R),
//                 explored or in sight (G), lit (B), each feathered *outward* only so a live square
//                 is always fully clear; B = the focused hero's sight (G) and the depth of the
//                 unexplored dark (B, 0 at its edge → 1 three squares in); B.r is unused
//   veilLook      the fog's palette and character from its veils (night / underground / weather)
//   frontier      squares out of sight that touch a square in sight (where wisps drift)
//   markAge       how a remembered foe's ghost fades with the pulses since it was sensed
//   unitChanges   who entered and who left the party's sight between two boards
import { blindFog, type FogView, type FogVeil, type FogMark, type Terrain } from '../../core/sim/types';

export type RGB = readonly [number, number, number];

/** Mask texels per square (the shader samples them linearly, so feathering is smooth). */
export const SUB = 4;
/** How far (in squares) each mask feathers out of its region. Sight stays tight; memory is softer. */
export const FEATHER = { sight: 0.42, explored: 0.45, lit: 0.7, focus: 0.35 } as const;
/** Depth of the unexplored dark: full at this many squares from anything the party has seen. */
export const DEPTH_SQUARES = 3;

const smooth = (e0: number, e1: number, x: number): number => { const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

/**
 * Distance (in squares) from every texel centre (SUB per square) to the nearest square where `on`
 * holds — the euclidean distance to that square's rectangle, 0 inside it — searched out to `reach`
 * squares; Infinity beyond.
 */
export function distanceField(w: number, h: number, on: (i: number) => boolean, reach: number, sub = SUB): Float32Array {
  const gw = w * sub, gh = h * sub;
  const out = new Float32Array(gw * gh);
  const r = Math.ceil(reach);
  // (the same distances as a scan of every square in reach for every sample, done per square: the
  // squares that are on are read once, each square's on-neighbours gathered once for all its
  // samples, and a square with none in reach skipped — a twenty-player board refogs every pulse)
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) mask[i] = on(i) ? 1 : 0;
  // docs/147 PG8: the nearest point of the set to a texel outside it lies on the set's edge, so only
  // squares on its edge (one side open, or at the board's edge) can be nearest; a square inside the
  // set is never nearer than the edge square that shares its nearest point (a twenty-player crawl's
  // explored rooms are mostly inside squares, and the unexplored dark's depth looks three squares out)
  const rim = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (!mask[i]) continue;
    rim[i] = x === 0 || y === 0 || x === w - 1 || y === h - 1 || !mask[i - 1] || !mask[i + 1] || !mask[i - w] || !mask[i + w] ? 1 : 0;
  }
  const near: number[] = [];
  for (let cy = 0; cy < h; cy++) for (let cx = 0; cx < w; cx++) {
    if (mask[cy * w + cx]) {
      for (let sy = 0; sy < sub; sy++) out.fill(0, (cy * sub + sy) * gw + cx * sub, (cy * sub + sy) * gw + cx * sub + sub);
      continue;
    }
    near.length = 0;
    for (let y = Math.max(0, cy - r); y <= Math.min(h - 1, cy + r); y++) for (let x = Math.max(0, cx - r); x <= Math.min(w - 1, cx + r); x++) if (rim[y * w + x]) near.push(x, y);
    for (let sy = 0; sy < sub; sy++) {
      const gy = cy * sub + sy, py = (gy + 0.5) / sub;
      for (let sx = 0; sx < sub; sx++) {
        const gx = cx * sub + sx, px = (gx + 0.5) / sub;
        let best = Infinity, bx = 0, by = 0;
        for (let k = 0; k < near.length; k += 2) {
          const x = near[k]!, y = near[k + 1]!;
          const dx = Math.max(x - px, 0, px - (x + 1)), dy = Math.max(y - py, 0, py - (y + 1));
          const d2 = dx * dx + dy * dy;
          if (d2 < best) { best = d2; bx = dx; by = dy; }
        }
        out[gy * gw + gx] = best === Infinity ? Infinity : Math.hypot(bx, by);
      }
    }
  }
  return out;
}

/**
 * One feathered channel at SUB texels per square: 1 inside a square of `on`, falling to 0 at
 * `feather` squares outside it (euclidean distance to the nearest such square's rectangle).
 */
export function featherChannel(w: number, h: number, on: (i: number) => boolean, feather: number, sub = SUB): Float32Array {
  const d = distanceField(w, h, on, feather, sub);
  for (let i = 0; i < d.length; i++) d[i] = d[i] === Infinity ? 0 : 1 - smooth(0, feather, d[i]!);
  return d;
}

export interface FogMasks { gw: number; gh: number; a: Uint8ClampedArray; b: Uint8ClampedArray }

/**
 * The shader's masks as RGBA bytes (alpha always 255: a canvas premultiplies anything less).
 * `focus` is the focused unit's own sight (or none).
 */
export function fogMasks(fog: Pick<FogView, 'w' | 'h' | 'cells' | 'lit'>, focus?: ReadonlySet<number>, sub = SUB): FogMasks {
  const { w, h } = fog;
  const { sight, known, lit, depth } = baseChannels(fog, sub);
  const foc = focus && focus.size ? featherChannel(w, h, (i) => focus.has(i), FEATHER.focus, sub) : undefined;
  const gw = w * sub, gh = h * sub;
  const a = new Uint8ClampedArray(gw * gh * 4), b = new Uint8ClampedArray(gw * gh * 4);
  for (let gy = 0; gy < gh; gy++) for (let gx = 0; gx < gw; gx++) {
    const g = gy * gw + gx, o = g * 4;
    a[o] = sight[g]! * 255; a[o + 1] = Math.max(known[g]!, sight[g]!) * 255; a[o + 2] = lit[g]! * 255; a[o + 3] = 255;
    b[o] = 0;
    b[o + 1] = foc ? foc[g]! * 255 : 0;
    const dd = depth[g]!;
    b[o + 2] = (dd === Infinity ? 1 : smooth(0, DEPTH_SQUARES, dd)) * 255;
    b[o + 3] = 255;
  }
  return { gw, gh, a, b };
}

/**
 * The channels that come from the fog alone (sight, explored, lit, depth), kept for the last fog
 * drawn: docs/147 PG8 — hovering a hero changes only the focus channel, and every hover rebuilt all
 * five fields of a twenty-player board. Kept against a copy of the fog's squares, so a fog changed
 * in place is never mistaken for the last one.
 */
let lastBase: { w: number; h: number; sub: number; cells: Uint8Array; lit: Uint8Array; out: { sight: Float32Array; known: Float32Array; lit: Float32Array; depth: Float32Array } } | undefined;
function baseChannels(fog: Pick<FogView, 'w' | 'h' | 'cells' | 'lit'>, sub: number): { sight: Float32Array; known: Float32Array; lit: Float32Array; depth: Float32Array } {
  const { w, h } = fog, n = w * h;
  const k = lastBase;
  if (k && k.w === w && k.h === h && k.sub === sub && fog.cells.length === n && fog.lit.length === n) {
    let same = true;
    for (let i = 0; i < n && same; i++) same = k.cells[i] === (fog.cells[i] ?? 0) && k.lit[i] === (fog.lit[i] ?? 0);
    if (same) return k.out;
  }
  const out = {
    sight: featherChannel(w, h, (i) => fog.cells[i] === 2, FEATHER.sight, sub),
    known: featherChannel(w, h, (i) => (fog.cells[i] ?? 0) > 0, FEATHER.explored, sub),
    lit: featherChannel(w, h, (i) => fog.lit[i] === 1, FEATHER.lit, sub),
    depth: distanceField(w, h, (i) => (fog.cells[i] ?? 0) > 0, DEPTH_SQUARES, sub),
  };
  const cells = new Uint8Array(n), litc = new Uint8Array(n);
  for (let i = 0; i < n; i++) { cells[i] = fog.cells[i] ?? 0; litc[i] = fog.lit[i] ?? 0; }
  lastBase = { w, h, sub, cells, lit: litc, out };
  return out;
}

/** Linear blend of two mask byte arrays into `out` (the fog rolling back between boards). */
export function blendMasks(from: Uint8ClampedArray, to: Uint8ClampedArray, k: number, out: Uint8ClampedArray): Uint8ClampedArray {
  const t = Math.max(0, Math.min(1, k));
  for (let i = 0; i < to.length; i++) out[i] = from[i]! + (to[i]! - from[i]!) * t;
  return out;
}

// ---- the look --------------------------------------------------------------------------------

export interface FogLook {
  /** The veil that sets the fog's character (night / underground / weather / rooms / clear). */
  key: FogVeil['key'] | 'clear';
  /** Unexplored dark, its lighter wisps, the explored memory veil, a pale highlight (wisps, ghosts), light falling in the fog. */
  deep: RGB; wisp: RGB; memory: RGB; rim: RGB; warm: RGB;
  /** Unexplored coverage (1: nothing shows through); memory wash and darkening. */
  dense: number; memWash: number; memDark: number;
  /** Drift speed (1 = a slow breathing fog) and how much the fog is streaked along the wind. */
  speed: number; stretch: number;
  /** Particle character: frontier wisps, ember motes at the light's edge, big mist curls. */
  wisps: boolean; embers: boolean; curls: boolean;
}

const LOOKS: Record<FogLook['key'], FogLook> = {
  // a smoky warm black that torchlight bites into; embers dance at the light's edge
  underground: { key: 'underground', deep: [0.04, 0.035, 0.037], wisp: [0.27, 0.22, 0.19], memory: [0.17, 0.145, 0.13], rim: [0.5, 0.41, 0.33], warm: [1.0, 0.58, 0.24],
    dense: 1, memWash: 0.52, memDark: 0.6, speed: 0.6, stretch: 1, wisps: true, embers: true, curls: false },
  // blue-black under the moon, silver wisps drifting at the edge of sight
  night: { key: 'night', deep: [0.018, 0.03, 0.065], wisp: [0.27, 0.33, 0.5], memory: [0.14, 0.18, 0.3], rim: [0.68, 0.78, 0.98], warm: [1.0, 0.64, 0.32],
    dense: 1, memWash: 0.52, memDark: 0.58, speed: 0.8, stretch: 1.2, wisps: true, embers: false, curls: false },
  // pale drifting mist that swallows the far squares
  weather: { key: 'weather', deep: [0.56, 0.6, 0.64], wisp: [0.84, 0.87, 0.9], memory: [0.66, 0.7, 0.74], rim: [0.95, 0.96, 0.98], warm: [1.0, 0.8, 0.55],
    dense: 1, memWash: 0.72, memDark: 0.16, speed: 1.4, stretch: 1.8, wisps: true, embers: false, curls: true },
  // docs/63: a puzzle room's closed doors — what lies behind them is simply dark, a still near-black
  // with a few slow wisps at the doorway; nothing is ever remembered dimly (a room once seen stays known)
  rooms: { key: 'rooms', deep: [0.03, 0.03, 0.04], wisp: [0.22, 0.22, 0.26], memory: [0.3, 0.31, 0.34], rim: [0.62, 0.64, 0.7], warm: [1.0, 0.7, 0.4],
    dense: 1, memWash: 0.5, memDark: 0.5, speed: 0.5, stretch: 1, wisps: true, embers: false, curls: false },
  // a fight under fog of war in plain day: slate haze, a cool memory
  clear: { key: 'clear', deep: [0.07, 0.08, 0.11], wisp: [0.34, 0.38, 0.45], memory: [0.36, 0.4, 0.46], rim: [0.86, 0.89, 0.94], warm: [1.0, 0.7, 0.4],
    dense: 1, memWash: 0.54, memDark: 0.54, speed: 0.9, stretch: 1.2, wisps: true, embers: false, curls: false },
};

const mixRGB = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** The weather veil takes the weather's own colour: blown sand, snow, rain, or plain mist. */
const MISTS: Record<'sand' | 'snow' | 'rain', Pick<FogLook, 'deep' | 'wisp' | 'memory' | 'rim'>> = {
  sand: { deep: [0.5, 0.4, 0.28], wisp: [0.86, 0.74, 0.55], memory: [0.66, 0.57, 0.43], rim: [0.96, 0.87, 0.68] },
  snow: { deep: [0.64, 0.68, 0.75], wisp: [0.93, 0.95, 0.98], memory: [0.74, 0.78, 0.84], rim: [1, 1, 1] },
  rain: { deep: [0.3, 0.34, 0.4], wisp: [0.6, 0.65, 0.72], memory: [0.46, 0.5, 0.56], rim: [0.8, 0.84, 0.9] },
};
export function mistFor(weatherKind: string | undefined): keyof typeof MISTS | undefined {
  if (weatherKind === 'sandstorm' || weatherKind === 'heat') return 'sand';
  if (weatherKind === 'snow' || weatherKind === 'blizzard' || weatherKind === 'sleet' || weatherKind === 'frost') return 'snow';
  if (weatherKind === 'rain' || weatherKind === 'storm') return 'rain';
  return undefined;
}

/**
 * The fog's look from its veils (and the weather, which colours a weather veil). Underground beats
 * night (no sky to light it); weather stacked on either greys its wisps and memory toward mist,
 * speeds the drift and curls it.
 */
export function veilLook(veils: ReadonlyArray<Pick<FogVeil, 'key'>>, weatherKind?: string): FogLook {
  const has = (k: FogVeil['key']) => veils.some((v) => v.key === k);
  const tint = mistFor(weatherKind);
  const weather: FogLook = tint ? { ...LOOKS.weather, ...MISTS[tint] } : LOOKS.weather;
  // docs/63: closed doors are the whole fog of a puzzle room (no sky veils it)
  if (has('rooms')) return { ...LOOKS.rooms };
  const base = has('underground') ? LOOKS.underground : has('night') ? LOOKS.night : has('weather') ? weather : LOOKS.clear;
  if (base.key === 'weather' || !has('weather')) return { ...base };
  const mist = weather;
  return { ...base, wisp: mixRGB(base.wisp, mist.wisp, 0.35), memory: mixRGB(base.memory, mist.memory, 0.25), rim: mixRGB(base.rim, mist.rim, 0.4),
    speed: Math.max(base.speed, 1.2), stretch: Math.max(base.stretch, 1.6), curls: true };
}

/**
 * docs/118 PT12: a crawl's look — the never-seen dark is the scene's `backdrop` itself, flat, with
 * no wisps or glow in it, so where the edge covers what was never seen (`closedDoorsField`) the
 * fog's feather runs into the very same colour and nothing marks where the board goes on.
 */
export function blindLook(look: FogLook, backdrop: number): FogLook {
  const c: RGB = [((backdrop >> 16) & 255) / 255, ((backdrop >> 8) & 255) / 255, (backdrop & 255) / 255];
  return { ...look, deep: c, wisp: c, dense: 1 };
}

// ---- closed doors (docs/63) ---------------------------------------------------------------------

type Field<T> = { w: number; h: number; tiles: ReadonlyArray<T | undefined> };

/**
 * docs/63 RF6: under a puzzle room's closed doors, the field as the party knows it — every square it
 * has never seen is no board (`Void`), so the edge layer paints the backdrop there and hangs a bank
 * under the rooms it knows, exactly as round the field's own edge: behind a shut door there is
 * nothing to see, not even the outline of the rooms. Any other fog, or none, gives the board itself.
 */
export function closedDoorsField<T extends { terrain: Terrain }>(board: Field<T>, fog: Pick<FogView, 'w' | 'h' | 'cells' | 'veils' | 'blind'> | undefined): Field<T | { terrain: Terrain }> {
  // docs/118 PT12: a crawl's fog hides the same way (`blindFog`)
  if (!fog || fog.w !== board.w || fog.h !== board.h || !blindFog(fog)) return board;
  return { w: board.w, h: board.h, tiles: board.tiles.map((t, i) => (fog.cells[i] === 0 ? { terrain: 'Void' as Terrain } : t)) };
}

// ---- the frontier ------------------------------------------------------------------------------


/** Squares out of sight with a square in sight beside them (4-neighbourhood), row-major. */
export function frontier(fog: Pick<FogView, 'w' | 'h' | 'cells'>): number[] {
  const { w, h, cells } = fog, out: number[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    if (cells[i] === 2) continue;
    // (plain reads, no closure per square: it runs on every board)
    if ((x + 1 < w && cells[i + 1] === 2) || (x > 0 && cells[i - 1] === 2) || (y + 1 < h && cells[i + w] === 2) || (y > 0 && cells[i - w] === 2)) out.push(i);
  }
  return out;
}

// ---- marks ---------------------------------------------------------------------------------------

export interface MarkAge {
  /** Pulses since it was sensed (0 = this pulse). */
  age: number;
  /** Ghost / ripple opacity: fresh marks read clearly, old ones fade to a floor. */
  alpha: number;
  /** Little pips under the ghost, one per pulse of age (capped). */
  pips: number;
}

export const MARK_FLOOR = 0.22;
export const MARK_PIPS = 4;

export function markAge(mark: Pick<FogMark, 'pulse'>, nowPulse: number): MarkAge {
  const age = Math.max(0, nowPulse - mark.pulse);
  return { age, alpha: Math.max(MARK_FLOOR, 0.62 * Math.pow(0.78, age)), pips: Math.min(MARK_PIPS, age) };
}

// ---- units in and out of sight ------------------------------------------------------------------

/** Unit ids that appear in `next` but not `prev` (entered sight), and the reverse (left it). */
export function unitChanges(prev: Iterable<number>, next: Iterable<number>): { entered: number[]; left: number[] } {
  const p = new Set(prev), n = new Set(next);
  return { entered: [...n].filter((id) => !p.has(id)), left: [...p].filter((id) => !n.has(id)) };
}
